import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { IconBell, useClickOutside } from "./Shell";
import { endpoints, type Meeting } from "../lib/api";
import { upcomingTime } from "../lib/format";

interface Notice {
  id: string;
  kind: "admit" | "soon" | "new" | "ready" | "shared";
  text: string;
  detail: string;
  to?: string;
}

const READ_KEY = "notifications-read";
const SEEN_KEY = "calendar-seen";
const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** The bell: things worth knowing, worked out from the meetings the app
 * already has — a bot waiting to be let in, a meeting about to start, a new
 * one on your calendar (a new invite), a transcript that's ready. What you've
 * read is remembered in this browser. */
export function Notifications() {
  const [open, setOpen] = useState(false);
  const [read, setRead] = useState<Set<string>>(() => new Set(load(READ_KEY)));
  const box = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(box, open, close);

  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });
  const meetings = useQuery({ queryKey: ["meetings"], queryFn: endpoints.meetings, refetchInterval: 15_000 });
  const calendar = useQuery({ queryKey: ["recordings"], queryFn: endpoints.recordings, refetchInterval: 60_000 });

  // Calendar meetings seen before. The first time, everything already there
  // counts as seen, so a new person isn't greeted by thirty "new" meetings.
  const [seen] = useState<Set<string> | null>(() => {
    const saved = load(SEEN_KEY);
    return saved.length ? new Set(saved) : null;
  });

  const notices = useMemo(
    () => build(meetings.data ?? [], calendar.data?.upcoming ?? [], seen, me.data?.bot_name ?? "the bot"),
    [meetings.data, calendar.data, seen, me.data?.bot_name],
  );
  const unread = notices.filter((notice) => !read.has(notice.id)).length;

  function toggle() {
    const opening = !open;
    setOpen(opening);
    if (opening) {
      // Opening the list is reading it.
      const now = new Set([...read, ...notices.map((notice) => notice.id)]);
      setRead(now);
      save(READ_KEY, [...now].slice(-300));
      save(SEEN_KEY, (calendar.data?.upcoming ?? []).map(calendarKey));
    }
  }

  // Remember the calendar as it is the first time, quietly.
  if (seen === null && calendar.data) save(SEEN_KEY, calendar.data.upcoming.map(calendarKey));

  return (
    <div className="topbar-menu" ref={box}>
      <button
        className="topbar-button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} new` : "Notifications"}
        title="Notifications"
      >
        <IconBell />
        {unread > 0 && <span className="badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="menu notices" role="dialog" aria-label="Notifications">
          <p className="menu-title">Notifications</p>
          {notices.length === 0 ? (
            <p className="notices-empty">Nothing new. Bots waiting to be let in, meetings about to start and new invites show up here.</p>
          ) : (
            <ul>
              {notices.map((notice) => {
                const body = (
                  <>
                    <span className={`notice-dot ${notice.kind}`} aria-hidden />
                    <span>
                      <span className="notice-text">{notice.text}</span>
                      <span className="notice-detail">{notice.detail}</span>
                    </span>
                  </>
                );
                return (
                  <li key={notice.id}>
                    {notice.to ? (
                      <Link className="notice" to={notice.to} onClick={close}>{body}</Link>
                    ) : (
                      <div className="notice">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function build(meetings: Meeting[], upcoming: Meeting[], seen: Set<string> | null, botName: string): Notice[] {
  const now = Date.now();
  const notices: Notice[] = [];
  for (const meeting of meetings) {
    const title = meeting.title ?? "A meeting";
    if (meeting.active && meeting.state === "waiting") {
      notices.push({
        id: `admit-${meeting.session_id}`,
        kind: "admit",
        text: `Admit "${botName}" in ${title}`,
        detail: "The bot is waiting to be let in.",
      });
    }
    // A colleague shared it with you, this past week.
    if (meeting.shared_by && meeting.shared_at && meeting.session_id
        && now - new Date(meeting.shared_at).getTime() < 7 * DAY) {
      notices.push({
        id: `shared-${meeting.session_id}`,
        kind: "shared",
        text: `${meeting.shared_by} shared ${title} with you`,
        detail: "The recording, transcript and minutes.",
        to: `/meetings/${meeting.session_id}`,
      });
    }
    const finished = new Date(meeting.starts_at ?? meeting.requested_at ?? 0).getTime();
    if (!meeting.active && meeting.transcript === "ready" && now - finished < DAY && meeting.session_id) {
      notices.push({
        id: `ready-${meeting.session_id}`,
        kind: "ready",
        text: `Transcript ready: ${title}`,
        detail: "Minutes follow in a minute or two.",
        to: `/meetings/${meeting.session_id}`,
      });
    }
  }
  for (const meeting of upcoming) {
    if (!meeting.starts_at) continue;
    const starts = new Date(meeting.starts_at).getTime();
    const title = meeting.title ?? "A meeting";
    if (starts > now && starts - now < HOUR) {
      notices.push({
        id: `soon-${calendarKey(meeting)}`,
        kind: "soon",
        text: `Starting soon: ${title}`,
        detail: `${upcomingTime(meeting.starts_at)} · ${meeting.state === "cancelled" ? "not being recorded" : "the bot will join"}`,
        to: "/calendar",
      });
    } else if (seen && !seen.has(calendarKey(meeting))) {
      notices.push({
        id: `new-${calendarKey(meeting)}`,
        kind: "new",
        text: `New on your calendar: ${title}`,
        detail: upcomingTime(meeting.starts_at),
        to: "/calendar",
      });
    }
  }
  return notices;
}

function calendarKey(meeting: Meeting): string {
  return `${meeting.meet_url}|${meeting.starts_at}`;
}

function load(key: string): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function save(key: string, values: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(values));
  } catch {
    /* remembering what was read is a nicety */
  }
}
